import { z } from 'zod';
import { PolicyRuleStatusSchema } from './approval_verdict';

/**
 * What the TRAVELER may read about how their request was decided (#5249) — the
 * policy that was applied, rule by rule, and the options the quotation presented.
 * Plucked from the mirror-only triage block on every read (like the pending
 * approvers, #4459): the dynamic per-client policy flags, the price benchmark and
 * the approver chain stay with the approver.
 */

const RuleTextSchema = z.object({ name: z.string(), description: z.string() });

/** ONE rule the policy evaluated: `WITHIN` it met, `WITHOUT` it broke, `NOT_APPLY` it skipped. */
export const VoucherPolicyRuleSchema = z.object({
  status: PolicyRuleStatusSchema,
  pt: RuleTextSchema,
  es: RuleTextSchema,
  en: RuleTextSchema,
});
export type VoucherPolicyRule = z.infer<typeof VoucherPolicyRuleSchema>;

/** The policy applied to the request: the group's name and EVERY rule it evaluated. */
export const VoucherPolicySchema = z.object({
  groupName: z.string().nullable(),
  rules: z.array(VoucherPolicyRuleSchema),
});
export type VoucherPolicy = z.infer<typeof VoucherPolicySchema>;

const QuotationTotalSchema = z.object({
  value: z.number().nullable(),
  currency: z.string().nullable(),
});

/** The flight a quotation candidate offers. `durationSec` is in seconds (the quotation's
 *  unit, unlike the segment's source-formatted string). Shared with the approver triage. */
export const QuotationFlightFactsSchema = z.object({
  departingCity: z.string().nullable(),
  departingAt: z.string().nullable(),
  arrivalCity: z.string().nullable(),
  arrivalAt: z.string().nullable(),
  flightNumber: z.string().nullable(),
  durationSec: z.number().nullable(),
  airline: z.string().nullable(),
  codeCia: z.string().nullable(),
  stops: z.number().nullable(),
});

/** One flight option the quotation presented: who sells it, for how much, and whether it
 *  was within the policy. */
export const VoucherFlightOptionSchema = z.object({
  supplier: z.string().nullable(),
  total: QuotationTotalSchema.nullable(),
  flight: QuotationFlightFactsSchema.nullable(),
  within: z.boolean().nullable(),
});
export type VoucherFlightOption = z.infer<typeof VoucherFlightOptionSchema>;

const FlightLegOptionsSchema = z.object({
  cheapest: VoucherFlightOptionSchema.nullable(),
  expensive: VoucherFlightOptionSchema.nullable(),
});

/** The options presented when the trip was requested: the flight's chosen option and the
 *  cheapest and most expensive per leg (`bundled` = one fare for both legs); the hotel's
 *  list of offers. */
export const VoucherQuotationSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('FLIGHT'),
    bundled: z.boolean().nullable(),
    chosen: z
      .object({
        departing: VoucherFlightOptionSchema.nullable(),
        returning: VoucherFlightOptionSchema.nullable(),
      })
      .nullable(),
    summary: z
      .object({
        departing: FlightLegOptionsSchema.nullable(),
        returning: FlightLegOptionsSchema.nullable(),
      })
      .nullable(),
  }),
  z.object({
    kind: z.literal('HOTEL'),
    options: z.array(
      z.object({ supplier: z.string().nullable(), total: QuotationTotalSchema.nullable() }),
    ),
  }),
]);
export type VoucherQuotation = z.infer<typeof VoucherQuotationSchema>;
